import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Input, Label, Modal, TextArea } from "semantic-ui-react";

import { DATE_FORMATS_FNS, Dropdown, PriceInput } from "components";
import "components/InterventionSubitemsModal/components/SubitemsModal/SubitemsModal.scss";
import { INTERVENTION_ELEMENT_TYPES } from "models";
import { InterventionElementIcon } from "modules/AppointmentDetails/components/Interventions/components/Subitems/components/InterventionElementIcon";
import { enumToDropdownOptions } from "utils/common";
import { formatCurrentUTCDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { InterventionElement } from "models";

type SubitemsModalProps = {
  isLoading?: boolean;
  element?: InterventionElement | null;
  onSubmit: (element: InterventionElement) => void;
  onClose?: () => void;
};

const TYPES_WITHOUT_PRICE = [INTERVENTION_ELEMENT_TYPES.Text, INTERVENTION_ELEMENT_TYPES.Rental];
const TYPES_WITH_MANDATORY_PRICE = [INTERVENTION_ELEMENT_TYPES.Discount, INTERVENTION_ELEMENT_TYPES.Fee, INTERVENTION_ELEMENT_TYPES.Menu];

export const SubitemsModal = ({ onClose, onSubmit, element, isLoading }: SubitemsModalProps) => {
  const { t } = useTranslation();

  const [elementType, setElementType] = useState<INTERVENTION_ELEMENT_TYPES>(element?.element_type || INTERVENTION_ELEMENT_TYPES.Text);
  const [brandCode, setBrandCode] = useState(element?.brand_code || "");
  const [referenceCode, setReferenceCode] = useState(element?.reference_code || "");
  const [description, setDescription] = useState(element?.description || "");
  const [quantity, setQuantity] = useState<number | null>(element?.quantity ?? null);
  const [price, setPrice] = useState<number | null>(element?.price ?? null);
  const [hasValidationError, setHasValidationError] = useState(false);

  const elementTypeOptions = enumToDropdownOptions({ enumObj: INTERVENTION_ELEMENT_TYPES, t }).map(option => ({
    ...option,
    text: (
      <div className="element-dropdown-option">
        <Label icon className="element-dropdown-icon">
          <InterventionElementIcon elementType={option.value} />
        </Label>
        {option.text}
      </div>
    )
  }));

  const supportsPriceFields = !TYPES_WITHOUT_PRICE.includes(elementType);
  const hasMandatoryPrice = TYPES_WITH_MANDATORY_PRICE.includes(elementType);

  const validateForm = () => {
    if (!description) return false;
    if (hasMandatoryPrice && (price === null || quantity === null)) return false;
    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      setHasValidationError(true);
      return;
    }

    onSubmit({
      element_type: elementType,
      brand_code: brandCode,
      reference_code: referenceCode,
      description,
      quantity: supportsPriceFields ? quantity : null,
      price: supportsPriceFields ? Number(price) : null,
      dms_nr: element?.dms_nr || `claire_${formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)}`,
      created_on: element?.created_on || formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
      is_local: true,
      labor_minutes: null,
      parent_dms_nr: "",
      id: element?.id ?? -1,
      intervention_id: element?.intervention_id ?? -1,
      vat: element?.vat ?? null,
      status: element?.status ?? 0
    });
  };

  const handleQuantityChange = (value: string) => setQuantity(isNaN(parseFloat(value)) ? null : parseFloat(value));
  const handlePriceChange = ({ value }: { value?: number }) => setPrice(value ?? null);

  return (
    <Modal open size="tiny" onClose={onClose} className="InterventionSubitemsModal">
      <Modal.Header>{element ? t("v8_edit_subitem", "Edit subitem") : t("v8_add_subitem", "Add Subitem")}</Modal.Header>
      <Modal.Content>
        <Form id="new-subitem">
          <Form.Field>
            <label>{t("v8_type", "Type")}</label>
            <Dropdown
              fluid
              selection
              options={elementTypeOptions}
              value={elementType}
              onChange={elementType => elementType && setElementType(elementType)}
              placeholder={t("v8_select_type", "Select type")}
              disabled
            />
          </Form.Field>

          {elementType !== INTERVENTION_ELEMENT_TYPES.Text && (
            <>
              <Form.Field>
                <label>{t("v8_brand", "Brand")}</label>
                <Input fluid value={brandCode} onChange={(_, data) => setBrandCode(data.value)} placeholder={t("v8_enter_brand", "Enter brand")} />
              </Form.Field>

              <Form.Field>
                <label>{t("v8_code", "Code")}</label>
                <Input fluid value={referenceCode} onChange={(_, data) => setReferenceCode(data.value)} placeholder={t("v8_enter_code", "Enter code")} />
              </Form.Field>
            </>
          )}

          <Form.Group>
            <Form.Field error={hasValidationError && !description} width={16} required>
              <label>{t("v8_description", "Description")}</label>
              <TextArea
                value={description}
                placeholder={t("v8_enter_description", "Enter description")}
                rows={2}
                onChange={event => setDescription(event.target.value)}
              />
            </Form.Field>
          </Form.Group>

          {supportsPriceFields && (
            <Form.Group widths="equal">
              <Form.Field required={hasMandatoryPrice} error={hasValidationError && hasMandatoryPrice && quantity === null}>
                <label>{t("v8_quantity", "Quantity")}</label>
                <Input fluid value={quantity} onChange={(_, data) => handleQuantityChange(data.value)} placeholder={t("v8_enter_quantity", "Enter quantity")} />
              </Form.Field>

              <Form.Field required={hasMandatoryPrice} error={hasValidationError && hasMandatoryPrice && price === null}>
                <label>{t("v8_unit", "Unit")}</label>
                <PriceInput
                  fluid
                  value={formatPrice(price)}
                  onValueChange={handlePriceChange}
                  placeholder={t("v8_enter_price", "Enter price")}
                  label={{ basic: true, content: "€" }}
                  labelPosition="right"
                />
              </Form.Field>

              <Form.Field required={hasMandatoryPrice} error={hasValidationError && hasMandatoryPrice && price === null}>
                <label>{t("v8_total_price", "Total price")}</label>
                <PriceInput
                  fluid
                  className="total-price-input"
                  readOnly
                  value={formatPrice(price ? (quantity ?? 1) * price : null)}
                  label={{ basic: true, content: "€" }}
                  labelPosition="right"
                  placeholder={t("v8_total", "Total")}
                />
              </Form.Field>
            </Form.Group>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <div className="action-buttons">
          <Button className="light" onClick={onClose}>
            {t("v8_cancel", "Cancel")}
            <Icon className="xmark" />
          </Button>
          <Button color="green" form="new-subitem" type="button" onClick={handleSubmit} loading={isLoading} disabled={isLoading}>
            {t("v8_add", "Add")}
            <Icon className="check" />
          </Button>
        </div>
      </Modal.Actions>
    </Modal>
  );
};
